/**
 * RegistreActesPage — le registre unique des actes du maire (mig 259, B2).
 *
 * ─── POURQUOI UN SEUL ÉCRAN ─────────────────────────────────────────────────
 *
 * Le maire tient UN registre. Jusqu'ici il fallait regarder à deux endroits —
 * les arrêtés du service technique d'un côté, ceux de la RH de l'autre — sans
 * jamais voir la série complète, ni savoir combien d'actes la commune avait
 * pris dans l'année.
 *
 * ─── CE QUI RESTE EN PLACE ──────────────────────────────────────────────────
 *
 * Les deux écrans de rédaction existants continuent de fonctionner : cet écran
 * est le REGISTRE (consulter, faire avancer, imprimer), pas leur remplaçant.
 * Un acte repris affiche son origine, pour qu'on sache d'où il vient.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Alert, Button, Input, Field, Badge, ModuleHeader, type Module } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { ActeWorkflowFrise, type StatutFrise } from '@/components/ActeWorkflowFrise';
import { api, ApiException } from '@/lib/api';
import type { ActeRegistre, StatutActe, StatistiquesRegistre } from '@roronoa-collectivites/shared-types';
import {
  ScrollText, Search, FileDown, Loader2, ChevronRight, ArrowLeft, Send, Check, X,
  FilePlus2, Trash2,
} from 'lucide-react';

const STATUT_LABEL: Record<StatutActe, string> = {
  genere: 'Généré',
  relecture_juridique: 'En relecture',
  a_signer: 'À signer',
  signe: 'Signé',
  publie: 'Publié',
  annule: 'Annulé',
};

/** Les transitions offertes, alignées sur la garde du serveur. */
const SUITES: Record<StatutActe, StatutActe[]> = {
  genere: ['relecture_juridique', 'a_signer', 'annule'],
  relecture_juridique: ['a_signer', 'genere', 'annule'],
  a_signer: ['signe', 'relecture_juridique', 'annule'],
  signe: ['publie', 'annule'],
  publie: ['annule'],
  annule: [],
};

function frDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

/**
 * Le panneau « Arrêtés » d'une brique — ou le registre entier.
 *
 * ─── UN SEUL ÉCRAN POUR TOUTES LES BRIQUES ──────────────────────────────────
 *
 * Demande d'Henrico (28/07) : « je veux que ce soit pareil partout — les
 * filtres, les types, la façon de présenter, les prévisualisations. Si on met un
 * onglet arrêtés dans une autre brique, il faudra que ce soit la même
 * présentation. »
 *
 * D'où ce composant : la liste filtrable, la fiche avec la frise du circuit et
 * l'aperçu du document. Le monter ailleurs demande une ligne — c'est ce qui
 * garantit que la comptabilité, demain, n'aura pas un troisième écran différent.
 *
 * `brique` non renseignée = le registre complet de la commune.
 */
export function ActesBriquePanel({
  brique: briqueFixe,
  titre = 'Registre des actes',
  eyebrow = 'Actes du maire',
  /**
   * Monté DANS une page qui a déjà son bandeau et son en-tête (l'onglet d'une
   * brique). Sans ça, on empilait deux bandeaux mairie, deux boutons « Tableau
   * de bord », et un en-tête bleu au milieu d'une brique orange.
   */
  encastre = false,
  accent = 'tech',
}: {
  brique?: string;
  titre?: string;
  eyebrow?: string;
  encastre?: boolean;
  accent?: Module;
} = {}) {
  const navigate = useNavigate();
  const { confirm, dialogElement } = useConfirmDialog();
  const [actes, setActes] = useState<ActeRegistre[]>([]);
  const [stats, setStats] = useState<StatistiquesRegistre | null>(null);
  const [ouvert, setOuvert] = useState<ActeRegistre | null>(null);
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  // Quand la brique est imposée (onglet d'une brique), le filtre est gelé :
  // on est déjà chez soi. Sur le registre entier, il reste ouvert.
  const [brique, setBrique] = useState(briqueFixe ?? '');
  // Sur une commune qui produit des centaines d'actes par an, une liste sans
  // filtre est inutilisable. Même trio que l'onglet Modèles : recherche, brique,
  // type.
  const [typeActe, setTypeActe] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [commentaire, setCommentaire] = useState('');
  // Aperçu PDF en cadre. Le JWT ne peut pas passer dans un `src` d'iframe : on
  // récupère le document par fetch authentifié, puis on le sert en URL `blob:`
  // (CSP `frame-src blob:` ouverte le 28/07 — sans elle le cadre restait vide).
  const [apercuUrl, setApercuUrl] = useState<string | null>(null);
  const [apercuErreur, setApercuErreur] = useState<string | null>(null);
  const [delibVisee, setDelibVisee] = useState<
    { id: string; numero: string; objet: string; dateSeance: string | null } | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [liste, s] = await Promise.all([
        api.actes.list({ statut: statut || undefined,
                         brique: briqueFixe ?? (brique || undefined),
                         q: recherche.trim() || undefined }),
        api.actes.statistiques(briqueFixe).catch(() => null),
      ]);
      setActes(liste);
      if (s) setStats(s);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [statut, brique, recherche, briqueFixe]);
  useEffect(() => { void charger(); }, [charger]);

  /**
   * D4-02 — la délibération que cet acte VISE.
   *
   * Le lien existait en base depuis la mig 259 mais ne remontait nulle part.
   * C'est pourtant la question qu'on se pose en relisant un arrêté : « sur quoi
   * s'appuie-t-il ? ». Sans la réponse, il fallait ouvrir le registre et
   * chercher le numéro à la main.
   *
   * `null` n'est PAS une erreur : la plupart des arrêtés ne visent aucune
   * délibération. On n'affiche alors rien du tout.
   */
  useEffect(() => {
    if (!ouvert) { setDelibVisee(null); return; }
    let annule = false;
    void (async () => {
      try {
        const r = await api.actes.deliberation(ouvert.id);
        if (!annule) setDelibVisee(r.deliberation);
      } catch {
        // Silencieux : ne pas savoir quelle délibération est visée ne doit pas
        // empêcher de lire l'acte.
        if (!annule) setDelibVisee(null);
      }
    })();
    return () => { annule = true; };
  }, [ouvert]);

  // Charge l'aperçu à l'ouverture d'une fiche, et le libère en sortant.
  useEffect(() => {
    if (!ouvert) { setApercuUrl(null); return; }
    let annule = false;
    let url: string | null = null;
    setApercuErreur(null);
    void (async () => {
      try {
        const blob = await api.actes.pdfBlob(ouvert.id);
        if (annule) return;
        url = URL.createObjectURL(blob);
        setApercuUrl(url);
      } catch (e) {
        if (!annule) setApercuErreur(e instanceof ApiException ? e.message : 'Aperçu indisponible');
      }
    })();
    return () => {
      annule = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [ouvert]);

  const types = useMemo(
    () => [...new Set(actes.map((a) => a.typeActe).filter(Boolean) as string[])].sort(),
    [actes]
  );

  /** Le filtre « Type » agit sur la liste déjà chargée. */
  const actesVisibles = useMemo(
    () => (typeActe ? actes.filter((a) => a.typeActe === typeActe) : actes),
    [actes, typeActe]
  );

  const briques = useMemo(
    () => [...new Set(actes.map((a) => a.brique).filter(Boolean) as string[])].sort(),
    [actes]
  );

  async function avancer(a: ActeRegistre, vers: StatutActe) {
    setBusy(true); setError(null); setMsg(null);
    try {
      const maj = await api.actes.transition(a.id, vers, commentaire.trim() || null);
      setOuvert(maj);
      setCommentaire('');
      setMsg(`Acte ${maj.numeroAffiche} — ${STATUT_LABEL[maj.statut].toLowerCase()}.`);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Transition impossible');
    } finally { setBusy(false); }
  }

  async function imprimer(a: ActeRegistre) {
    setError(null);
    try {
      const blob = await api.actes.pdfBlob(a.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'PDF indisponible');
    }
  }

  // ── Fiche ─────────────────────────────────────────────────────────────────
  if (ouvert) {
    const a = ouvert;
    return (
      <div className={encastre ? 'text-ink' : 'min-h-screen text-ink'}>
        {!encastre && <AppHeader module={titre} accent={accent} />}
        {!encastre && (
          <ModuleHeader accent={accent} icon={ScrollText}
            eyebrow={a.numeroAffiche} title={a.titre} />
        )}
        {/* Encastré, le numéro et le titre tiennent sur une ligne : la page
            hôte porte déjà son bandeau et son en-tête. */}
        {encastre && (
          <div className="mb-3 flex flex-wrap items-baseline gap-2 border-b border-border pb-2">
            <span className="font-mono text-xs text-ink-subtle">{a.numeroAffiche}</span>
            <span className="font-medium text-ink">{a.titre}</span>
          </div>
        )}

        <main id={encastre ? undefined : 'main-content'} className={encastre ? 'mt-4' : 'mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8'}>
          {error && <Alert variant="error" className="mb-4">{error}</Alert>}
          {msg && <Alert variant="success" className="mb-4">{msg}</Alert>}

          <Button variant="glass" size="sm" className="mb-4"
            onClick={() => { setOuvert(null); setMsg(null); }}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour au registre
          </Button>

          <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
            <Card variant="glass" padded>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={a.statut === 'annule' ? 'urgent' : 'neutral'} size="sm">
                  {STATUT_LABEL[a.statut]}
                </Badge>
                {a.brique && <Badge variant="neutral" size="sm">{a.brique}</Badge>}
                {/* D4-02 — le visa, lisible sans quitter la fiche. */}
                {delibVisee && (
                  <Badge variant="info" size="sm">
                    vise {delibVisee.numero}
                  </Badge>
                )}
                {a.origineTable && (
                  <span className="text-xs text-ink-subtle">
                    repris de {a.origineTable === 'arretes' ? 'Service technique' : 'RH'}
                  </span>
                )}
              </div>

              {a.objet && <p className="mt-3 text-sm text-ink-muted">{a.objet}</p>}

              {/* Les deux formes cohabitent : structurée ou corps libre. */}
              {a.considerants.length > 0 && (
                <ul className="mt-4 space-y-1">
                  {a.considerants.map((c, i) => (
                    <li key={i} className="text-sm text-ink-muted">{c}</li>
                  ))}
                </ul>
              )}
              {a.articles.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {a.articles.map((art) => (
                    <li key={art.numero} className="text-sm text-ink">
                      <span className="font-medium">Article {art.numero}</span> — {art.texte}
                    </li>
                  ))}
                </ul>
              )}
              {a.corps && (
                <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded-r-lg border border-border bg-surface/40 p-3 font-sans text-sm text-ink-muted">
                  {a.corps}
                </pre>
              )}

              {/* Le document tel qu'il sortira. Même disposition que la fiche du
                  service technique : le PDF à gauche, le circuit à droite. */}
              <div className="mt-4">
                <h3 className="text-xs uppercase tracking-wide text-ink-subtle">Document</h3>
                {apercuErreur ? (
                  <Alert variant="error" className="mt-2">{apercuErreur}</Alert>
                ) : apercuUrl ? (
                  <iframe
                    src={apercuUrl}
                    title={`Aperçu de l'acte ${a.numeroAffiche}`}
                    className="mt-2 h-[36rem] w-full rounded-r-lg border border-border bg-white"
                  />
                ) : (
                  <div className="mt-2 flex h-40 items-center justify-center gap-2 rounded-r-lg border border-border bg-surface/40 text-sm text-ink-muted">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Génération de l'aperçu…
                  </div>
                )}
              </div>
            </Card>

            <div className="space-y-4">
              <Card variant="glass" padded>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="text-xs uppercase tracking-wide text-ink-subtle">
                    Workflow validation
                  </h2>
                  <Badge
                    variant={a.statut === 'annule' ? 'urgent'
                      : a.statut === 'signe' || a.statut === 'publie' ? 'success' : 'warning'}
                    size="sm"
                  >
                    {STATUT_LABEL[a.statut]}
                  </Badge>
                </div>

                {/* La frise partagée : même présentation que la fiche du service
                    technique, demandée par la mairie « de partout ». */}
                <ActeWorkflowFrise
                  statut={a.statut as StatutFrise}
                  etapes={[
                    { label: 'Généré',
                      sub: `${a.brique ?? 'Commune'}`,
                      date: a.createdAt },
                    { label: 'Relecture juridique',
                      sub: a.relectureEnvoyeeNom
                        ? `Demandée à ${a.relectureEnvoyeeNom}`
                        : 'Secrétaire général',
                      date: a.relectureEnvoyeeAt },
                    { label: 'Signature',
                      sub: `${a.maireTitre ?? 'Le Maire'}${a.maireNom ? `, ${a.maireNom}` : ''}`,
                      date: a.signeAt },
                    { label: 'Publication',
                      sub: a.publieAt ? 'Publié' : 'Affichage · contrôle de légalité',
                      date: a.publieAt },
                  ]}
                />

                {a.reluCommentaire && (
                  <p className="mt-3 rounded-r-lg border border-border bg-surface/40 p-2 text-xs text-ink-muted">
                    « {a.reluCommentaire} »
                  </p>
                )}

                {SUITES[a.statut].length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-border pt-4">
                    <Field label="Commentaire" htmlFor="cmt"
                      hint="Repris dans la trace de l'étape">
                      <Input id="cmt" value={commentaire}
                        onChange={(e) => setCommentaire(e.target.value)}
                        placeholder="Facultatif" />
                    </Field>
                    {/* Voie manuscrite : le dépôt du scan FAIT la signature.
                        Sans lui, cliquer « Signé » changeait un statut sans
                        qu'aucun document signé n'existe. */}
                    {a.statut === 'a_signer' && (
                      <div className="rounded-r-lg border border-border bg-surface/40 p-2.5">
                        <label htmlFor="depot-signe"
                          className="block text-xs font-medium text-ink">
                          Déposer l'original signé
                        </label>
                        <p className="mt-0.5 text-2xs text-ink-subtle">
                          On l'imprime, le maire le signe, on scanne et on dépose
                          ici — l'acte passe alors en « signé ».
                        </p>
                        <input
                          id="depot-signe" type="file" accept="application/pdf"
                          disabled={busy}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            setBusy(true); setError(null);
                            try {
                              await api.rh.arretes.deposerSigneManuel(a.id, f);
                              setMsg(`Acte ${a.numeroAffiche} — original signé déposé.`);
                              const maj = await api.actes.get(a.id);
                              setOuvert(maj);
                              await charger();
                            } catch (err) {
                              setError(err instanceof ApiException
                                ? err.message : 'Dépôt impossible');
                            } finally { setBusy(false); }
                          }}
                          className="mt-2 w-full text-2xs text-ink-muted file:mr-2 file:h-7 file:rounded-r-lg file:border file:border-border file:bg-surface/60 file:px-2 file:text-2xs file:text-ink"
                        />
                      </div>
                    )}

                    {SUITES[a.statut].map((vers) => (
                      <Button
                        key={vers}
                        variant={vers === 'annule' ? 'glass' : 'primary'}
                        size="sm" className="w-full" loading={busy}
                        onClick={() => void avancer(a, vers)}
                      >
                        {vers === 'annule'
                          ? <X className="h-4 w-4" aria-hidden="true" />
                          : vers === 'signe' || vers === 'publie'
                            ? <Check className="h-4 w-4" aria-hidden="true" />
                            : <Send className="h-4 w-4" aria-hidden="true" />}
                        {STATUT_LABEL[vers]}
                      </Button>
                    ))}
                  </div>
                )}
              </Card>

              <Button variant="glass" size="sm" className="w-full" onClick={() => void imprimer(a)}>
                <FileDown className="h-4 w-4" aria-hidden="true" /> Lire / imprimer le PDF
              </Button>

              {/* La suppression n'existait que sur l'écran du service technique :
                  côté RH on pouvait annuler, jamais supprimer un brouillon.
                  Refusée sur un acte publié — le registre garde ce qui est sorti
                  de la mairie. */}
              {a.statut !== 'publie' && (
                <Button
                  variant="glass" size="sm" className="w-full" disabled={busy}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Supprimer l'acte ${a.numeroAffiche} ?`,
                      description: a.statut === 'signe'
                        ? 'Cet acte est signé. Le supprimer efface sa trace du registre — préférez l\'annulation si l\'acte est déjà sorti.'
                        : 'Il disparaîtra du registre. Son numéro ne sera pas réattribué.',
                      confirmLabel: 'Supprimer', danger: true,
                    });
                    if (!ok) return;
                    setBusy(true); setError(null);
                    try {
                      await api.actes.supprimer(a.id);
                      setOuvert(null);
                      setMsg(`Acte ${a.numeroAffiche} supprimé.`);
                      await charger();
                    } catch (e) {
                      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
                    } finally { setBusy(false); }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-urgent" aria-hidden="true" /> Supprimer
                </Button>
              )}
            </div>
          </div>
        </main>
        {dialogElement}
      </div>
    );
  }

  // ── Liste ─────────────────────────────────────────────────────────────────
  return (
    <div className={encastre ? 'text-ink' : 'min-h-screen text-ink'}>
      {!encastre && <AppHeader module={titre} accent={accent} />}
      {!encastre && (
        <ModuleHeader accent={accent} icon={ScrollText} eyebrow={eyebrow} title={titre} />
      )}

      <main id={encastre ? undefined : 'main-content'} className={encastre ? 'mt-4' : 'mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8'}>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {/* Ni bandeau explicatif ni bouton « Nouvel acte » : le premier
            répétait une évidence, le second menait vers un écran supprimé. La
            rédaction se fait dans l'onglet Arrêtés de la brique. */}

        {stats && (
          <Card variant="glass" padded className="mb-4">
            <div className="flex flex-wrap items-baseline gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-ink-subtle">
                  Actes {stats.annee}
                </div>
                <div className="text-3xl font-semibold tabular-nums text-ink">{stats.total}</div>
              </div>
              {Object.entries(stats.parStatut).map(([s, n]) => (
                <div key={s} className="text-sm text-ink-muted">
                  <span className="font-medium text-ink">{n}</span>{' '}
                  {STATUT_LABEL[s as StatutActe]?.toLowerCase() ?? s}
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Field label="Rechercher" htmlFor="q">
              <Input id="q" value={recherche} onChange={(e) => setRecherche(e.target.value)}
                placeholder="Numéro, titre, objet…" />
            </Field>
          </div>
          <div className="min-w-[160px]">
            <Field label="Statut" htmlFor="st">
              <select id="st" value={statut} onChange={(e) => setStatut(e.target.value)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="">Tous</option>
                {(Object.keys(STATUT_LABEL) as StatutActe[]).map((s) => (
                  <option key={s} value={s}>{STATUT_LABEL[s]}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="min-w-[180px]">
            <Field label="Type" htmlFor="ty">
              <select id="ty" value={typeActe} onChange={(e) => setTypeActe(e.target.value)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="">Tous</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          {!briqueFixe && briques.length > 0 && (
            <div className="min-w-[160px]">
              <Field label="Brique" htmlFor="br">
                <select id="br" value={brique} onChange={(e) => setBrique(e.target.value)}
                  className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                  <option value="">Toutes</option>
                  {briques.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
          </div>
        ) : actesVisibles.length === 0 ? (
          <Card variant="glass" padded>
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Search className="h-4 w-4" aria-hidden="true" />
              Aucun acte ne correspond.
            </p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {actesVisibles.map((a) => (
              <li key={a.id}>
                <button
                  type="button" onClick={() => { setOuvert(a); setMsg(null); }}
                  className="flex w-full items-center justify-between gap-3 rounded-r-lg border border-border bg-surface/55 p-3 text-left backdrop-blur-glass transition-all hover:border-tech/40 hover:-translate-y-0.5"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-ink-subtle">{a.numeroAffiche}</span>
                      <Badge variant={a.statut === 'annule' ? 'urgent' : 'neutral'} size="sm">
                        {STATUT_LABEL[a.statut]}
                      </Badge>
                      {a.brique && <Badge variant="neutral" size="sm">{a.brique}</Badge>}
                    </span>
                    <span className="mt-0.5 block font-medium text-ink">{a.titre}</span>
                    {a.objet && (
                      <span className="block truncate text-xs text-ink-subtle">{a.objet}</span>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
      {dialogElement}
    </div>
  );
}
